"use client"

import Link from "next/link"
import {CircleDot, Box} from "lucide-react"
import type {ResolvedPerson, ResolvedChangeArtifact, CatalogueFeature} from "@devplaninc/codeup"
import {FeatureRelationship} from "@devplaninc/codeup"
import {knowledgeFeatureCataloguePath} from "@/app/paths"
import {GitProvider} from "@/pb/api/devplan/types/integrations"
import {GitProviderIcon} from "@/app/components/repositories/GitProviderIcon"
import {prUrl, repoProvider} from "@/app/util/gitUtil"

export const FEATURE_RELATIONSHIP_LABELS: Record<FeatureRelationship, string> = {
  [FeatureRelationship.FEATURE_RELATIONSHIP_UNSPECIFIED]: "Related",
  [FeatureRelationship.FEATURE_RELATIONSHIP_CREATED]: "Created",
  [FeatureRelationship.FEATURE_RELATIONSHIP_UPDATED]: "Updated",
  [FeatureRelationship.FEATURE_RELATIONSHIP_DEPRECATED]: "Deprecated",
}

export function splitArtifactsByTarget(artifacts: ResolvedChangeArtifact[]) {
  const featureArtifacts: ResolvedChangeArtifact[] = []
  const otherArtifacts: ResolvedChangeArtifact[] = []

  for (const artifact of artifacts) {
    if (artifact.target?.$case === "feature") {
      featureArtifacts.push(artifact)
      continue
    }
    otherArtifacts.push(artifact)
  }

  return {featureArtifacts, otherArtifacts}
}

function FeatureArtifactBadge({artifact, featureMap, companyId}: {
  artifact: ResolvedChangeArtifact,
  featureMap: Map<string, CatalogueFeature>,
  companyId: number,
}) {
  if (artifact.target?.$case !== "feature") return null

  const {featureId, relationship} = artifact.target.value
  const feature = featureMap.get(featureId)
  if (!feature) return null

  const label = feature.title || featureId.slice(0, 8)
  const relLabel = FEATURE_RELATIONSHIP_LABELS[relationship]

  return (
    <Link
      href={`${knowledgeFeatureCataloguePath(companyId)}?feature=${encodeURIComponent(featureId)}`}
      className="inline-flex max-w-full items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-xs text-blue-600 transition-colors hover:bg-blue-500/20 dark:text-blue-400"
    >
      <Box className="size-3 shrink-0"/>
      <span className="break-words whitespace-normal">{label}</span>
      {relLabel && <span className="text-blue-400 dark:text-blue-500">· {relLabel}</span>}
    </Link>
  )
}

export function InlineFeatureBadges({artifacts, featureMap, companyId}: {
  artifacts: ResolvedChangeArtifact[],
  featureMap: Map<string, CatalogueFeature>,
  companyId: number,
}) {
  if (artifacts.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {artifacts.map((artifact, i) => (
        <FeatureArtifactBadge
          key={`feature-${i}`}
          artifact={artifact}
          featureMap={featureMap}
          companyId={companyId}
        />
      ))}
    </div>
  )
}

export function ArtifactBadges({artifacts, featureMap, companyId, repoProviderMap}: {
  artifacts: ResolvedChangeArtifact[],
  featureMap: Map<string, CatalogueFeature>,
  companyId: number,
  repoProviderMap: Record<string, GitProvider> | undefined,
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {artifacts.map((a, i) => {
        if (!a.target) return null
        switch (a.target.$case) {
          case "pullRequest": {
            const {repo, number} = a.target.value
            const provider = repoProvider(repo, repoProviderMap)
            return (
              <a
                key={i}
                href={prUrl(repo, number, provider)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <GitProviderIcon provider={provider} className="size-3"/>{repo.split("/").pop()}#{number}
              </a>
            )
          }
          case "gitCommit":
            return null
          case "issue":
            return (
              <span key={i} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                <CircleDot className="size-3"/>{a.target.value.project}-{a.target.value.id}
              </span>
            )
          case "feature": {
            return (
              <FeatureArtifactBadge
                key={i}
                artifact={a}
                featureMap={featureMap}
                companyId={companyId}
              />
            )
          }
          default:
            return null
        }
      })}
    </div>
  )
}

export function PeopleBadges({people, persons}: {people: {personId: string}[], persons: Record<string, ResolvedPerson>}) {
  const seen = new Set<string>()
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {people.map(ref => {
        const p = persons[ref.personId]
        if (!p) return null
        const display = p.name || p.email || p.gitUsername || p.personId
        if (seen.has(display)) return null
        seen.add(display)
        return (
          <span key={p.personId} className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {display}
          </span>
        )
      })}
    </div>
  )
}
